import { zodResolver } from '@hookform/resolvers/zod';
import { Stack, Typography } from '@mui/material';
import Button, {
  ButtonSize,
  ButtonVariant,
} from '@packages/shared-ui/v4/button';
import {
  type PlaceResult,
  RhfAddressField,
  RhfCurrencyField,
  SubmitErrorMessage,
  TextFieldSize,
} from '@packages/shared-ui/v4/form';
import * as m from 'motion/react-m';
import { useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { number, object, type infer as zodInfer } from 'zod';
import Icon, { IconName } from '@/components/Icon/Icon';
import { toFiveDigitZip, useLses } from '@/hooks/priceOfPower/useLses';
import {
  usePriceOfPowerLseIdParam,
  usePriceOfPowerMonthlyBill,
  usePriceOfPowerPlace,
  usePriceOfPowerViewParam,
} from '@/hooks/priceOfPower/usePriceOfPowerUrlState';
import { useLoadTariffs } from '@/hooks/priceOfPower/useTariffs';
import {
  autocompleteAddressValidator,
  InputName,
  lastBillValidator,
} from '@/utils/validation/inputs';
import {
  type PriceOfPowerIntakeType,
  trackPriceOfPowerAddressSelected,
  trackPriceOfPowerIntakeError,
  trackPriceOfPowerIntakeSubmitted,
  trackPriceOfPowerPersonalView,
  trackPriceOfPowerUtilitySelected,
} from '../analytics';
import { attachSheetKeyboard } from '../BottomSheet/attachSheetKeyboard';
import { useStyles } from './FormContent.styles';
import { RhfLseDropdown } from './RhfLseDropdown';
import { UtilitySlot, useSlotTransition } from './UtilitySlot';

const GOOGLE_MAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_API_KEY ?? '';
const ADDRESS_GROW_65_35 = 13 / 7;
const TARIFFS_LOAD_ERROR = `We couldn't load utility rates for this address. Please try again later.`;

const formSchema = object({
  ...autocompleteAddressValidator,
  ...lastBillValidator,
  [InputName.UTILITY]: number().int().positive().nullable(),
});

export type FormSchema = zodInfer<typeof formSchema>;

const inputSize = {
  xs: TextFieldSize.Small,
  md: TextFieldSize.Large,
};

export function IntakeForm({
  intakeType,
  submitLabel,
  onSubmitted,
}: {
  intakeType: PriceOfPowerIntakeType;
  /** Switches to the labeled layout: inputs with visible labels and a full-width submit button. */
  submitLabel?: string;
  /** Replaces opening the interactive report, so a host can show the report itself. */
  onSubmitted?: () => void;
}) {
  const { classes } = useStyles();
  const slotTransition = useSlotTransition();
  const [urlPlace, setPlace] = usePriceOfPowerPlace();
  const [urlBill, setMonthlyBill] = usePriceOfPowerMonthlyBill();
  const [lseIdParam, setLseIdParam] = usePriceOfPowerLseIdParam();
  const [, setView] = usePriceOfPowerViewParam();
  const [selectedPlace, setSelectedPlace] = useState(urlPlace);
  const zipCode = toFiveDigitZip(selectedPlace?.address.zipCode);
  const { lses, isFetching: isFetchingLses } = useLses(zipCode);

  const [loadTariffs] = useLoadTariffs();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const requiresUtilitySelection = Boolean(selectedPlace) && lses.length > 1;

  const form = useForm<FormSchema>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: {
      [InputName.AUTOCOMPLETE_ADDRESS]: urlPlace?.formattedAddress ?? '',
      [InputName.LAST_BILL]: urlBill?.toString() ?? '',
      [InputName.UTILITY]: lseIdParam ?? null,
    },
  });

  const {
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors, isValid },
  } = form;

  const utility = watch(InputName.UTILITY);
  const isUtilitySelected =
    utility != null && lses.some((lse) => lse.lseId === utility);

  const onSubmit: SubmitHandler<FormSchema> = async ({ lastBill, utility }) => {
    if (!selectedPlace || isSubmitting) return;

    const monthlyBill = Number.parseInt(lastBill, 10);
    const lseId =
      lses.find((lse) => lse.lseId === utility)?.lseId ??
      (lses.length === 1 ? lses[0].lseId : null);

    if (!zipCode || lseId == null || !Number.isInteger(lseId) || lseId <= 0) {
      trackPriceOfPowerIntakeError('missing_lse');
      setError('root.formSubmitError', {
        type: 'missing_lse',
        message: TARIFFS_LOAD_ERROR,
      });
      return;
    }

    setPlace(selectedPlace);
    void setMonthlyBill(monthlyBill);
    void setLseIdParam(lseId);

    clearErrors('root.formSubmitError');
    setIsSubmitting(true);
    try {
      await loadTariffs({ zipCode, lseId }, false).unwrap();
      trackPriceOfPowerIntakeSubmitted({
        monthlyBill,
        utilitySelection: requiresUtilitySelection
          ? 'user'
          : lses.length === 1
            ? 'auto'
            : 'none',
        intakeType,
      });
      trackPriceOfPowerPersonalView();
      if (onSubmitted) {
        setIsSubmitting(false);
        onSubmitted();
        return;
      }
      setView('report');
    } catch {
      trackPriceOfPowerIntakeError('tariffs');
      setError('root.formSubmitError', {
        type: 'tariffs',
        message: TARIFFS_LOAD_ERROR,
      });
      setIsSubmitting(false);
    }
  };

  const handlePlaceSelected = (selected: PlaceResult) => {
    clearErrors('root.formSubmitError');
    setSelectedPlace(selected);
    setValue(InputName.UTILITY, null, { shouldValidate: true });
    const zip = toFiveDigitZip(selected.address.zipCode);
    if (!zip) return;
    trackPriceOfPowerAddressSelected({
      zip,
      state: selected.address.state,
    });
  };

  const handleAddressInputChange = () => {
    clearErrors('root.formSubmitError');
    if (!selectedPlace) return;
    setSelectedPlace(null);
    setValue(InputName.UTILITY, null, { shouldValidate: true });
    setPlace(null);
    void setMonthlyBill(null);
  };

  const handleUtilityChange = (lseId: number | null) => {
    const lse = lses.find((item) => item.lseId === lseId);
    const zip = toFiveDigitZip(selectedPlace?.address.zipCode);
    if (!lse || !zip) return;
    trackPriceOfPowerUtilitySelected({
      zip,
      lseId: lse.lseId,
      lseName: lse.name,
    });
  };

  const isLabeled = Boolean(submitLabel);
  const isSubmitDisabled =
    (!isValid && !errors.root?.formSubmitError) ||
    !selectedPlace ||
    isFetchingLses ||
    isSubmitting ||
    (requiresUtilitySelection && !isUtilitySelected);

  const addressField = (
    <RhfAddressField<FormSchema>
      name={InputName.AUTOCOMPLETE_ADDRESS}
      {...(isLabeled
        ? { label: 'Address' }
        : {
            placeholder: 'Enter your address...',
            'aria-label': 'Address',
            prefixIcon: IconName.SEARCH,
          })}
      fullWidth
      suffix={
        isFetchingLses ? (
          <output aria-label='Loading utilities'>
            <Icon
              name={IconName.LOADING_RING}
              width={{ xs: 16, md: 24 }}
              color='currentColor'
              classname={classes.addressSpinnerIcon}
              aria-hidden
            />
          </output>
        ) : null
      }
      googleMapsApiKey={GOOGLE_MAPS_API_KEY}
      onPlaceSelected={handlePlaceSelected}
      onInputChange={handleAddressInputChange}
      inputSize={inputSize}
    />
  );

  const billField = (
    <RhfCurrencyField<FormSchema>
      className={classes.rowField}
      name={InputName.LAST_BILL}
      {...(isLabeled
        ? { label: 'Monthly bill' }
        : {
            placeholder: 'Avg. monthly utility bill',
            'aria-label': 'Average monthly utility bill',
          })}
      fullWidth
      inputSize={inputSize}
    />
  );

  const utilityDropdown = (
    <RhfLseDropdown<FormSchema>
      name={InputName.UTILITY}
      placeholder={isLabeled ? 'Select' : 'Utility'}
      label='Utility'
      hideLabel={!isLabeled}
      fullWidth
      inputSize={inputSize}
      lses={lses}
      onValueChange={handleUtilityChange}
    />
  );

  return (
    <FormProvider {...form}>
      <Stack
        component='form'
        ref={attachSheetKeyboard}
        className={classes.form}
        gap={0}
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        {isLabeled ? (
          <div className={classes.labeledFields}>
            <m.div
              className={classes.addressSlot}
              initial={false}
              animate={{
                flexGrow: requiresUtilitySelection ? 2 : ADDRESS_GROW_65_35,
              }}
              transition={slotTransition}
            >
              {addressField}
            </m.div>
            {billField}
            <UtilitySlot
              isOpen={requiresUtilitySelection}
              className={classes.utilitySlot}
            >
              {utilityDropdown}
            </UtilitySlot>
          </div>
        ) : (
          <>
            {addressField}
            <Stack direction='row' gap={0} alignItems='flex-start' width='100%'>
              {billField}
              <UtilitySlot
                isOpen={requiresUtilitySelection}
                className={classes.utilitySlot}
              >
                {utilityDropdown}
              </UtilitySlot>
              <Button
                className={classes.submit}
                type='submit'
                variant={ButtonVariant.PRIMARY}
                size={{ xs: ButtonSize.SM, md: ButtonSize.MD }}
                iconName={IconName.ARROW_RIGHT}
                aria-label='See your personal report'
                disabled={isSubmitDisabled}
                isLoading={isSubmitting}
              />
            </Stack>
          </>
        )}
        {isLabeled && (
          <Button
            className={classes.labeledSubmit}
            type='submit'
            variant={ButtonVariant.PRIMARY}
            size={{ xs: ButtonSize.SM, md: ButtonSize.MD }}
            disabled={isSubmitDisabled}
            isLoading={isSubmitting}
          >
            {submitLabel}
          </Button>
        )}
        {errors.root?.formSubmitError && (
          <div className={classes.submitError}>
            <SubmitErrorMessage>
              {errors.root.formSubmitError?.message}
            </SubmitErrorMessage>
          </div>
        )}
      </Stack>
    </FormProvider>
  );
}

export default function FormContent() {
  const { classes } = useStyles();

  return (
    <Stack gap={1}>
      <Stack gap={-1}>
        <Typography variant='h3'>Utility rates are rising...</Typography>
        <Typography variant='bodyMd' className={classes.subtitle}>
          Explore past 5 year regional rate increases across the U.S., or enter
          your address to see your personal report
        </Typography>
      </Stack>
      <IntakeForm intakeType='default' />
    </Stack>
  );
}
